import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder } from '@angular/forms';

import { atLeastOneValidator, DialogRef } from '@hglobal/common';
import { environment } from '@pt/environment';
import { FormType, UserProfile } from '@pt/models';

export interface TeamMemberBonusForm {
  userId: number;
  amount: number;
  comment: string;
}

@Component({
  standalone: false,
  selector: 'pu-bonus-form',
  templateUrl: './bonus-form.component.html',
  styleUrls: ['./bonus-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BonusFormComponent implements OnInit {
  functionalUsers = input<UserProfile[]>();
  bonusTotal = input<number>();

  readonly destroyRef = inject(DestroyRef);
  readonly coinIconName = `${environment.shortName}-coin`;

  bonusesForm: FormArray<FormType<TeamMemberBonusForm>>;
  bonusLeft: number;
  commentsState: Record<string, any> = {};

  constructor(private _fb: FormBuilder, private _dialogRef: DialogRef) {
  }

  ngOnInit() {
    this.bonusLeft = this.bonusTotal();
    this.bonusesForm = this._initBonusesForm();

    this.bonusesForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: values => {
          this.bonusLeft = this.bonusTotal() - values.filter(value => value.amount > 0)
            .reduce((acc, cur) => acc + cur.amount, 0);
        },
      });
  }

  accrueEqually() {
    const remainder = this.bonusTotal() % this.bonusesForm.controls.length;
    this.bonusesForm.controls.forEach(group => group.patchValue({
      amount: (this.bonusTotal() - remainder) / this.functionalUsers().length,
    }));
    this.bonusLeft = remainder;
  }

  reset() {
    this.bonusesForm.controls.forEach(group => group.patchValue({ amount: 0 }));
    this.bonusLeft = this.bonusTotal();
  }

  toggleComment(index: number) {
    this.commentsState[index] = !this.commentsState[index];
  }

  send(): void {
    this._dialogRef.close({
      type: 'bonus',
      payload: this.bonusesForm.value.filter(value => value.amount),
    });
  }

  close() {
    this._dialogRef.close();
  }

  private _initBonusesForm(): FormArray<FormType<TeamMemberBonusForm>> {
    return new FormArray<FormType<TeamMemberBonusForm>>(
      this.functionalUsers().map(({ id }) => this._fb.group<TeamMemberBonusForm>({
        userId: id,
        amount: 0,
        comment: '',
      })),
      atLeastOneValidator(control => control
        .controls.some(control => control.value.amount > 0)),
    );
  }
}
